import React, { FunctionComponent } from "react";

import {
  IBlog,
  IBlogPosts,
  IBlogPostsFields,
} from "@/types/generated/contentful";
import { ContentfulService } from "@/services/contentful";
import { BlogPostTile } from "./blogPostTile";
import Link from "next/link";

type Props = {
  content: IBlogPosts;
};

const BlogPosts: FunctionComponent<Props> = async ({ content }) => {
  const {
    heading,
    headingAlignment,
    tag,
    background,
    foreground,
    backgroundOverlayImage,
    backgroundOverlayType,
  } = content.fields as IBlogPostsFields;

  const fetchPosts = async () => {
    const contentfulService = new ContentfulService();
    return await contentfulService.getLatestBlogPosts(tag?.sys.id);
  };

  const posts = await fetchPosts();
  let headingAlign = "";
  switch (headingAlignment) {
    case "left":
      headingAlign = "text-left";
      break;
    case "center":
      headingAlign = "text-center";
      break;
    case "right":
      headingAlign = "text-right";
      break;
  }

  if (!posts || (posts && !posts.length)) return <></>;

  const renderPosts = () => {
    return (
      <>
        <div className="xl:flex-cols hidden w-full gap-9 xl:flex">
          <div className="flex max-w-2xl">
            <BlogPostTile
              key={posts[0].sys.id}
              leadingTile={true}
              content={posts[0] as IBlog}
            />
          </div>
          <div className="flex flex-col gap-4 xl:gap-9">
            <BlogPostTile key={posts[1].sys.id} content={posts[1] as IBlog} />
            <BlogPostTile key={posts[2].sys.id} content={posts[2] as IBlog} />
          </div>
        </div>
        <div className="flex xl:hidden">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <BlogPostTile key={posts[0].sys.id} content={posts[0] as IBlog} />
            <BlogPostTile key={posts[1].sys.id} content={posts[1] as IBlog} />
            <BlogPostTile key={posts[2].sys.id} content={posts[2] as IBlog} />
          </div>
        </div>
      </>
    );
  };

  return (
    <section
      className={`mx-auto mb-auto flex w-screen flex-col gap-4 px-4 py-8`}
      style={{
        backgroundColor: background || "transparent",
        ...(backgroundOverlayImage?.fields.file?.url && {
          backgroundImage:
            "url('" + backgroundOverlayImage?.fields.file?.url + "')",
        }),
        backgroundSize: backgroundOverlayType,
        ...(foreground && { color: foreground }),
      }}
    >
      <div className={`m-auto xl:container`}>
        {heading && (
          <div className={`flex flex-col`}>
            <h2
              className={`mb-4 !leading-tight tracking-tight lg:mb-6 ${headingAlign}`}
            >
              {heading}
            </h2>
          </div>
        )}
      </div>
      <div className="m-auto xl:container">
        {renderPosts()}
        <div className="w-full py-2 text-right">
          <Link
            className="w-full text-center text-base text-primary hover:text-dark-primary hover:underline"
            href="/insights"
          >
            View all insights
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPosts;
